import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { MessageCircleQuestion, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { MemoryCard } from "@/components/MemoryCard";
import { InstallAppButton } from "@/components/InstallAppButton";
import { CAPTURE_MODES } from "@/components/AppShell";
import { toast } from "sonner";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [memories, setMemories] = useState(null);
  useEffect(() => {
    const flagged = location.state?.suggest_recovery_codes || sessionStorage.getItem("ga_suggest_codes") === "1";
    if (!flagged || !user || user.auth_mode !== "username" || user.recovery_codes_left) return;
    sessionStorage.removeItem("ga_suggest_codes");
    window.history.replaceState({}, "");
    toast("Sua conta não tem e-mail. Gere códigos de recuperação para não perder o acesso se esquecer a senha.", { duration: 12000, action: { label: "Gerar agora", onClick: () => navigate("/seguranca") } });
  }, [location.state, navigate, user]);

  const load = async () => {
    try {
      const { data } = await api.get("/memories", { params: { limit: 6 } });
      setMemories(data.items);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  useEffect(() => { load(); }, []);

  const toggleFav = async (m) => { await api.put(`/memories/${m.id}`, { favorite: !m.favorite }); load(); };
  const archive = async (m) => { await api.put(`/memories/${m.id}`, { archived: true }); toast.success("Memória arquivada."); load(); };
  const remove = async (m) => { await api.delete(`/memories/${m.id}`); toast.success("Memória excluída."); load(); };

  const firstName = user?.name?.split(" ")[0];

  return (
    <div className="ga-fade-up px-5 pt-8">
      <div className="flex items-start justify-between">
        <div>
          <p className="ga-muted text-sm" data-testid="home-greeting">Olá, {firstName}!</p>
          <h1 className="mt-0.5 text-2xl font-semibold tracking-tight">O que você quer guardar?</h1>
        </div>
        <Link to="/perfil" className="ga-surface flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold" aria-label="Perfil" data-testid="home-avatar-button">
          {user?.name?.[0]?.toUpperCase()}
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {CAPTURE_MODES.map((m, i) => (
          <button
            key={m.key}
            data-testid={`btn-capture-${m.key === "voz" ? "falar" : m.key === "texto" ? "escrever" : m.key}`}
            onClick={() => navigate(`/nova/${m.key}`)}
            className="ga-fade-up flex h-28 flex-col items-center justify-center gap-2 rounded-3xl text-base font-semibold transition-transform hover:-translate-y-0.5 active:scale-[0.97]"
            style={{ background: m.bg, color: m.fg, animationDelay: `${i * 60}ms`, boxShadow: m.key === "documento" ? "none" : `0 12px 24px -12px ${m.bg}` }}
          >
            <m.icon size={28} strokeWidth={2.2} />
            {m.label}
          </button>
        ))}
      </div>

      <Link to="/perguntar" className="ga-card mt-4 flex items-center gap-3 p-4 transition-transform active:scale-[0.99]" data-testid="home-ask-button">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl" style={{ background: "#05324A", color: "#33D9B2" }}>
          <MessageCircleQuestion size={22} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Pergunte ao Clicou, Guardou!</div>
          <div className="ga-muted text-xs">“Onde coloquei meu passaporte?”</div>
        </div>
        <ChevronRight size={18} className="ga-muted" />
      </Link>

      <div className="mt-3" data-testid="home-install-app"><InstallAppButton /></div>

      <div className="mt-7 flex items-center justify-between">
        <h2 className="text-base font-semibold md:text-lg">Memórias recentes</h2>
        <Link to="/memorias" className="text-xs font-semibold" style={{ color: "#13C9A3" }} data-testid="home-see-all-link">Ver todas</Link>
      </div>

      <div className="mt-3 space-y-2.5" data-testid="home-recent-memories">
        {memories === null && [1, 2, 3].map((i) => <div key={i} className="ga-card h-[74px] animate-pulse" />)}
        {memories?.length === 0 && (
          <div className="ga-card p-6 text-center" data-testid="home-empty-state">
            <p className="text-sm font-medium">Nada guardado ainda.</p>
            <p className="ga-muted mt-1 text-xs">Toque em um dos botões acima e registre em segundos.</p>
          </div>
        )}
        {memories?.map((m) => <MemoryCard key={m.id} memory={m} onFavorite={toggleFav} onArchive={archive} onDelete={remove} />)}
      </div>
    </div>
  );
}
